import React, { useMemo, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import "../css/dashboard/baar.css"

const TargetComparisonChart = ({ doc, ec, nl }) => {
    const [timeRange, setTimeRange] = useState('thisMonth');

    const getFilteredCount = (dataArray, range, status) => {
        if (!dataArray || !Array.isArray(dataArray) || dataArray.length === 0) return 0;

        const now = new Date();
        let startDate;
        let endDate = new Date();

        if (range === 'thisMonth') {
            startDate = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
            endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
        } else if (range === 'prevMonth') {
            startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1, 0, 0, 0, 0);
            endDate = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999); 
        } else if (range === 'last3Months') {
            startDate = new Date(now.getFullYear(), now.getMonth() - 2, 1, 0, 0, 0, 0);
            endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
        }

        return dataArray.filter(item => {
            const dateStr = item.document_date || item.date;
            if (!dateStr || !item.docstatus) return false;

            let itemDate;
            if (dateStr.includes('-')) {
                const parts = dateStr.split('-');
                if (parts[0].length === 4) {
                    itemDate = new Date(dateStr);
                } else {
                    itemDate = new Date(parts[2], parts[1] - 1, parts[0]);
                }
            } else {
                itemDate = new Date(dateStr);
            }

            if (isNaN(itemDate.getTime())) return false;

            // Matches specific status (processing, completed, or rejected)
            return (
                item.docstatus.toLowerCase() === status.toLowerCase() &&
                itemDate >= startDate &&
                itemDate <= endDate
            );
        }).length;
    };

    const data = useMemo(() => {
        const multiplier = timeRange === 'last3Months' ? 3 : 1;

        const categories = [
            { name: 'Documents', raw: doc, target: 30 },
            { name: 'Encumbrance Certificate', raw: ec, target: 20 },
            { name: 'Nagal', raw: nl, target: 20 }
        ];

        return categories.map(cat => ({
            name: cat.name,
            Target: cat.target * multiplier,
            Completed: getFilteredCount(cat.raw, timeRange, 'completed'),
            Rejected: getFilteredCount(cat.raw, timeRange, 'rejected'),
            Processing: getFilteredCount(cat.raw, timeRange, 'processing')
        }));
    }, [doc, ec, nl, timeRange]);

    const rangeLabels = {
        thisMonth: "This Month",
        prevMonth: "Previous Month",
        last3Months: "Last 3 Months"
    };

    return (
        <div className="analytics-chart-card">
            <div className="analytics-header">
                <div className="analytics-title-group">
                    <h3 className="analytics-title">Work Performance Overview</h3>
                    <p className="analytics-subtitle">Stats for {rangeLabels[timeRange]}</p>
                </div>

                <div className="range-selector">
                    <select 
                        value={timeRange} 
                        onChange={(e) => setTimeRange(e.target.value)}
                        className="range-dropdown"
                    >
                        <option value="thisMonth">This Month</option>
                        <option value="prevMonth">Previous Month</option>
                        <option value="last3Months">Last 3 Months</option>
                    </select>
                </div>
            </div>
            
            <div className="analytics-chart-container">
                <ResponsiveContainer width="100%" height={350}>
                    <BarChart data={data} margin={{ top: 10, right: 10, left: -20, bottom: 0 }} barGap={5}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                        <XAxis 
                            dataKey="name" 
                            axisLine={false} 
                            tickLine={false} 
                            tick={{ fill: '#64748b', fontSize: 12, fontWeight: 600 }} 
                            dy={10}
                        />  
                        <YAxis axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 12 }} />
                        <Tooltip 
                            cursor={{ fill: '#f8fafc' }}
                            contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' }}
                        />
                        <Legend verticalAlign="top" align="right" iconType="circle" wrapperStyle={{paddingBottom: '20px'}}/>
                        
                        {/* Target Bar */}
                        <Bar name="Target" dataKey="Target" fill="#08182c" radius={[4, 4, 0, 0]} barSize={20} />
                        
                        {/* Completed Bar - Green */}
                        <Bar name="Completed" dataKey="Completed" fill="#10b981" radius={[4, 4, 0, 0]} barSize={20} />
                        
                        {/* Processing Bar - Blue */}
                        <Bar name="Processing" dataKey="Processing" fill="#3b82f6" radius={[4, 4, 0, 0]} barSize={20} />

                        {/* Rejected Bar - Red */}
                        <Bar name="Rejected" dataKey="Rejected" fill="#ef4444" radius={[4, 4, 0, 0]} barSize={20} />
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};

export default TargetComparisonChart;